import { useState, useEffect } from 'react'
import { Trash2, Edit, Save, X, AlertTriangle, CheckCircle, Users, Shield, Server, MapPin, Settings, Info, Eye, EyeOff } from 'lucide-react'
import api from '../services/api'

const NVRMassActions = ({ onSuccess, onClose, hasUpdatePermission, hasDeletePermission, onEditIndividualCamera }) => {
    const [action, setAction] = useState('') // 'edit' or 'delete'
    const [loading, setLoading] = useState(false)
    const [errors, setErrors] = useState({})
    const [roles, setRoles] = useState([])
    const [selectedLocations, setSelectedLocations] = useState([])
    const [groupingMode, setGroupingMode] = useState('location') // 'location', 'nvr', 'individual'
    const [cameras, setCameras] = useState([])
    const [fetchingCameras, setFetchingCameras] = useState(true)
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
    const [showPassword, setShowPassword] = useState(false)
    const [editData, setEditData] = useState({
        accessRoles: [],
        rolesChanged: false, // Track if roles were explicitly changed
        isActive: null, // null means no change
        manufacturer: '',
        model: '',
        resolution: { width: 1920, height: 1080 },
        fps: 25,
        bitrate: 2048,
        // New fields for mass editing
        ipAddress: '',
        port: '',
        username: '',
        password: '',
        url: '',
        // Location fields
        locationName: '',
        locationCity: ''
    })

    // Fetch all cameras when component mounts
    const fetchAllCameras = async () => {
        setFetchingCameras(true)
        try {
            const response = await api.get('/cameras?limit=10000') // Get all cameras
            setCameras(response.data.data.cameras || [])
        } catch (error) {
            console.error('Error fetching all cameras:', error)
            setErrors({ general: 'Failed to fetch cameras' })
        } finally {
            setFetchingCameras(false)
        }
    }

    const fetchRoles = async () => {
        try {
            const response = await api.get('/roles')
            setRoles(response.data.data.roles)
        } catch (error) {
            console.error('Error fetching roles:', error)
        }
    }

    // ALL useEffect hooks at the top level - never conditional
    useEffect(() => {
        fetchAllCameras()
    }, [])

    // Debug: Log camera data to understand structure
    useEffect(() => {
        console.log('Cameras data:', cameras)
        if (cameras.length > 0) {
            console.log('Sample camera location data:', cameras[0].location)
        }
    }, [cameras])

    // Fetch roles when component mounts
    useEffect(() => {
        fetchRoles()
    }, [])

    // Clear selected locations when grouping mode changes
    useEffect(() => {
        setSelectedLocations([])
    }, [groupingMode])

    // Populate editData with current values when edit action is triggered
    useEffect(() => {
        try {
            if (action === 'edit' && selectedLocations.length > 0) {
                const groupedCameras = getGroupedCameras()
                const camerasToUpdate = selectedLocations.flatMap(groupKey => groupedCameras.find(group => group.groupKey === groupKey)?.cameras || [])
                const currentCamera = camerasToUpdate[0] || {}
                
                console.log('Populating editData with camera:', currentCamera)
                
                                 // Reset editData when entering edit mode - don't depend on editData itself
                 setEditData({
                     accessRoles: currentCamera.accessRoles?.map(role => role._id) || [],
                     rolesChanged: false,
                     isActive: null,
                     manufacturer: currentCamera.manufacturer || '',
                     model: currentCamera.model || '',
                     resolution: currentCamera.resolution || { width: 1920, height: 1080 },
                     fps: currentCamera.fps || 25,
                     bitrate: currentCamera.bitrate || 2048,
                     ipAddress: currentCamera.ipAddress || '',
                     port: currentCamera.port || '',
                     username: currentCamera.username || '',
                     password: currentCamera.password || '',
                     url: currentCamera.url || '',
                     locationName: currentCamera.location?.exactLocation || '',
                     locationCity: currentCamera.location?.city || ''
                 })
            }
        } catch (error) {
            console.error('Error in editData useEffect:', error)
        }
    }, [action, selectedLocations, cameras])

    // Group cameras based on selected mode
    const getGroupedCameras = () => {
        if (!cameras || cameras.length === 0) {
            return []
        }
        
        if (groupingMode === 'individual') {
            // Group each camera individually
            return cameras.map((camera, index) => ({
                groupKey: camera._id || `camera-${index}-${camera.name || 'unnamed'}`,
                groupName: camera.name || 'Unnamed Camera',
                nvrKey: `${camera.ipAddress || 'unknown'}:${camera.port || 'unknown'}`,
                cameras: [camera]
            }))
        } else if (groupingMode === 'nvr') {
            // Group by NVR IP
            const nvrGroups = cameras.reduce((groups, camera) => {
                const nvrKey = `${camera.ipAddress || 'unknown'}:${camera.port || 'unknown'}`
                if (!groups[nvrKey]) {
                    groups[nvrKey] = {
                        groupKey: nvrKey,
                        groupName: `NVR ${nvrKey}`,
                        nvrKey: nvrKey,
                        cameras: []
                    }
                }
                groups[nvrKey].cameras.push(camera)
                return groups
            }, {})
            return Object.values(nvrGroups)
        } else {
            // Group by location (default)
            const locationGroups = cameras.reduce((groups, camera) => {
                let locationKey = 'Unknown Location'
                
                if (camera.location) {
                    if (camera.location.exactLocation && camera.location.exactLocation.trim() !== '') {
                        locationKey = camera.location.exactLocation.trim()
                    } else if (camera.location.city && camera.location.city.trim() !== '') {
                        locationKey = camera.location.city.trim()
                    }
                }
                
                if (locationKey === 'Unknown Location') {
                    const nameParts = (camera.name || '').split(' ')
                    if (nameParts.length >= 2) {
                        locationKey = `${nameParts[0]} ${nameParts[1]}`.trim()
                    }
                }
                
                const nvrKey = `${camera.ipAddress || 'unknown'}:${camera.port || 'unknown'}`
                const finalLocationKey = `${locationKey} (${nvrKey})`
                
                if (!groups[finalLocationKey]) {
                    groups[finalLocationKey] = {
                        groupKey: finalLocationKey,
                        groupName: locationKey,
                        nvrKey: nvrKey,
                        cameras: []
                    }
                }
                groups[finalLocationKey].cameras.push(camera)
                return groups
            }, {})
            return Object.values(locationGroups)
        }
    }

    const groupedCameras = getGroupedCameras()


    const handleLocationSelection = (locationKey) => {
        setSelectedLocations(prev => {
            if (prev.includes(locationKey)) {
                // Deselect
                return prev.filter(key => key !== locationKey)
            } else {
                // Select
                if (groupingMode === 'individual') {
                    // In individual mode, only allow one selection at a time
                    return [locationKey]
                } else {
                    // In other modes, allow multiple selections
                    return [...prev, locationKey]
                }
            }
        })
    }

    const handleSelectAllLocations = () => {
        if (selectedLocations.length === groupedCameras.length) {
            setSelectedLocations([])
        } else {
            setSelectedLocations(groupedCameras.map(group => group.groupKey))
        }
    }


    const handleMassDelete = async () => {
        console.log('handleMassDelete called')
        console.log('selectedLocations:', selectedLocations)
        console.log('groupedCameras:', groupedCameras)
        
        setShowDeleteConfirm(false)
        setLoading(true)
        try {
            const camerasToDelete = selectedLocations.flatMap(groupKey => 
                groupedCameras.find(group => group.groupKey === groupKey)?.cameras.map(camera => camera._id)
            )
            console.log('camerasToDelete:', camerasToDelete)
            
            const promises = camerasToDelete.map(cameraId => 
                api.delete(`/cameras/${cameraId}`)
            )
            await Promise.all(promises)
            onSuccess()
            onClose()
        } catch (error) {
            console.error('Error deleting cameras:', error)
            setErrors({ general: 'Failed to delete some cameras' })
        } finally {
            setLoading(false)
        }
    }

    const handleMassEdit = async () => {
        setLoading(true)
        try {
            const camerasToUpdate = selectedLocations.flatMap(groupKey => 
                groupedCameras.find(group => group.groupKey === groupKey)?.cameras
            )
            
            const promises = camerasToUpdate.map(camera => {
                const updateData = {}
                
                // Only update fields that have been changed
                if (editData.rolesChanged) {
                    updateData.accessRoles = editData.accessRoles
                }
                if (editData.isActive !== null) {
                    updateData.isActive = editData.isActive
                }
                if (editData.manufacturer) {
                    updateData.manufacturer = editData.manufacturer
                }
                if (editData.model) {
                    updateData.model = editData.model
                }
                if (editData.resolution.width !== 1920 || editData.resolution.height !== 1080) {
                    updateData.resolution = editData.resolution
                }
                if (editData.fps !== 25) {
                    updateData.fps = editData.fps
                }
                if (editData.bitrate !== 2048) {
                    updateData.bitrate = editData.bitrate
                }
                // New connection fields
                if (editData.ipAddress) {
                    updateData.ipAddress = editData.ipAddress
                }
                if (editData.port) {
                    updateData.port = parseInt(editData.port)
                }
                if (editData.username) {
                    updateData.username = editData.username
                }
                if (editData.password) {
                    updateData.password = editData.password
                }
                
                // Location fields
                if (editData.locationName || editData.locationCity) {
                    updateData.location = {
                        ...camera.location, // Keep existing location data
                        exactLocation: editData.locationName || camera.location?.exactLocation,
                        city: editData.locationCity || camera.location?.city
                    }
                }

                return api.put(`/cameras/${camera._id}`, updateData)
            })
            await Promise.all(promises)
            onSuccess()
            onClose()
        } catch (error) {
            console.error('Error updating cameras:', error)
            setErrors({ general: 'Failed to update some cameras' })
        } finally {
            setLoading(false)
        }
    }

    const handleRoleChange = (roleId) => {
        setEditData(prev => ({
            ...prev,
            accessRoles: prev.accessRoles.includes(roleId)
                ? prev.accessRoles.filter(id => id !== roleId)
                : [...prev.accessRoles, roleId],
            rolesChanged: true
        }))
    }

    const handleChange = (field, value) => {
        setEditData(prev => ({
            ...prev,
            [field]: value
        }))
    }

    const handleResolutionChange = (field, value) => {
        setEditData(prev => ({
            ...prev,
            resolution: {
                ...prev.resolution,
                [field]: parseInt(value)
            }
        }))
    }

    if (!action) {
        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[85vh] flex flex-col">
                    <div className="flex items-center justify-between p-6 bg-gray-50 border-b border-gray-200 flex-shrink-0">
                        <div className="flex items-center space-x-3">
                            <MapPin className="h-6 w-6 text-orange-600" />
                            <div>
                                <h2 className="text-xl font-semibold text-gray-900">
                                    Location-Based Mass Actions
                                </h2>
                                <p className="text-sm text-gray-500">
                                    Manage cameras grouped by location
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
                        >
                            <X className="h-5 w-5" />
                        </button>
                    </div>

                    <div className="flex-1 overflow-y-auto p-6">
                        {fetchingCameras ? (
                            <div className="flex items-center justify-center py-12">
                                <div className="text-center">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-400 mx-auto mb-4"></div>
                                    <p className="text-gray-600">Loading all cameras...</p>
                                </div>
                            </div>
                        ) : (
                            <>
                                {/* Grouping Mode Selector */}
                                <div className="mb-6">
                                    <h3 className="text-lg font-semibold text-gray-900 mb-3">
                                        Grouping Mode
                                    </h3>
                                    <div className="flex space-x-4">
                                        <label className="flex items-center space-x-2">
                                            <input
                                                type="radio"
                                                name="groupingMode"
                                                value="location"
                                                checked={groupingMode === 'location'}
                                                onChange={(e) => setGroupingMode(e.target.value)}
                                                className="text-indigo-600 focus:ring-indigo-500"
                                            />
                                            <span className="text-sm text-gray-700">By Location</span>
                                        </label>
                                        <label className="flex items-center space-x-2">
                                            <input
                                                type="radio"
                                                name="groupingMode"
                                                value="nvr"
                                                checked={groupingMode === 'nvr'}
                                                onChange={(e) => setGroupingMode(e.target.value)}
                                                className="text-indigo-600 focus:ring-indigo-500"
                                            />
                                            <span className="text-sm text-gray-700">By NVR</span>
                                        </label>
                                        <label className="flex items-center space-x-2">
                                            <input
                                                type="radio"
                                                name="groupingMode"
                                                value="individual"
                                                checked={groupingMode === 'individual'}
                                                onChange={(e) => setGroupingMode(e.target.value)}
                                                className="text-indigo-600 focus:ring-indigo-500"
                                            />
                                            <span className="text-sm text-gray-700">Individual Cameras (Edit one at a time)</span>
                                        </label>
                                    </div>
                                </div>

                                <div className="mb-6">
                                    
                                    
                                    <div className="flex items-center justify-between mb-4">
                                        <div>
                                            <h3 className="text-lg font-semibold text-gray-900">
                                                Select {groupingMode === 'individual' ? 'Cameras' : groupingMode === 'nvr' ? 'NVRs' : 'Locations'} to Manage
                                            </h3>
                                            <p className="text-sm text-gray-600 mt-1">
                                                Total cameras selected: {selectedLocations.flatMap(groupKey => 
                                                    groupedCameras.find(group => group.groupKey === groupKey)?.cameras || []
                                                ).length}
                                            </p>
                                        </div>
                                        {groupingMode !== 'individual' && (
                                            <button
                                                onClick={handleSelectAllLocations}
                                                className="text-sm text-blue-600 hover:text-blue-700"
                                            >
                                                {selectedLocations.length === groupedCameras.length ? 'Deselect All' : 'Select All'}
                                            </button>
                                        )}
                                    </div>
                                    
                                    <div className="space-y-3">
                                        {groupedCameras.map((group) => (
                                            <div key={group.groupKey} className="border border-gray-200 rounded-lg p-4">
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center space-x-3">
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedLocations.includes(group.groupKey)}
                                                            onChange={() => handleLocationSelection(group.groupKey)}
                                                            className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                                        />
                                                        <div>
                                                            <h4 className="font-medium text-gray-900">
                                                                {group.groupName}
                                                            </h4>
                                                            <p className="text-sm text-gray-600">
                                                                {group.cameras.length} camera(s) • {group.nvrKey}
                                                            </p>
                                                            <p className="text-xs text-gray-500">
                                                                Location: {group.cameras[0]?.location?.exactLocation || 'No exact location'} | {group.cameras[0]?.location?.city || 'No city'}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {selectedLocations.length > 0 && (
                                    <div className="space-y-3">
                                        {hasUpdatePermission && (
                                            <button
                                                onClick={() => {
                                                    if (groupingMode === 'individual' && selectedLocations.length === 1 && onEditIndividualCamera) {
                                                        // Get the selected camera for individual editing
                                                        const selectedCameraGroup = groupedCameras.find(group => group.groupKey === selectedLocations[0]);
                                                        const selectedCamera = selectedCameraGroup?.cameras[0];
                                                        if (selectedCamera) {
                                                            onEditIndividualCamera(selectedCamera);
                                                            onClose(); // Close this modal
                                                        } else {
                                                            // Camera not found - show error instead of silent failure
                                                            console.error('Selected camera not found:', selectedLocations[0]);
                                                            setErrors({ general: 'Selected camera not found. Please try again.' });
                                                        }
                                                    } else {
                                                        setAction('edit');
                                                    }
                                                }}
                                                className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                                            >
                                                <Edit className="h-4 w-4" />
                                                <span>
                                                    {groupingMode === 'individual' && selectedLocations.length === 1 
                                                        ? 'Edit Camera'
                                                        : `Edit Selected ${groupingMode === 'individual' ? 'Cameras' : groupingMode === 'nvr' ? 'NVRs' : 'Locations'} (${selectedLocations.length})`
                                                    }
                                                </span>
                                            </button>
                                        )}
                                        
                                        {hasDeletePermission && (
                                            <button
                                                onClick={() => setAction('delete')}
                                                className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                                            >
                                                <Trash2 className="h-4 w-4" />
                                                <span>Delete All Cameras from Selected {groupingMode === 'individual' ? 'Cameras' : groupingMode === 'nvr' ? 'NVRs' : 'Locations'}</span>
                                            </button>
                                        )}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>
        )
    }

    if (action === 'delete') {
        const camerasToDelete = selectedLocations.flatMap(groupKey => groupedCameras.find(group => group.groupKey === groupKey)?.cameras)
        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl">
                    <div className="p-6">
                        <div className="flex items-center space-x-3 mb-4">
                            <AlertTriangle className="h-6 w-6 text-red-600" />
                            <h2 className="text-xl font-semibold text-gray-900">
                                Confirm Location Deletion
                            </h2>
                        </div>
                        <p className="text-gray-600 mb-6">
                            Are you sure you want to delete all cameras from <strong>{selectedLocations.length}</strong> location(s)? 
                            This will delete <strong>{camerasToDelete.length}</strong> camera(s) and cannot be undone.
                        </p>
                        
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                            <h4 className="font-medium text-red-800 mb-2">Locations to be deleted:</h4>
                            <div className="max-h-32 overflow-y-auto space-y-2">
                                {selectedLocations.map(groupKey => {
                                    const group = groupedCameras.find(g => g.groupKey === groupKey)
                                    return (
                                        <div key={groupKey} className="text-sm text-red-700">
                                            <strong>{group?.groupName}</strong> - {group?.cameras.length} camera(s)
                                        </div>
                                    )
                                })}
                            </div>
                        </div>
                        
                        <div className="flex space-x-3">
                            <button
                                onClick={handleMassDelete}
                                disabled={loading}
                                className="flex-1 flex items-center justify-center space-x-2 px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-gray-400 transition-colors"
                            >
                                <Trash2 className="h-4 w-4" />
                                <span>Delete All Cameras</span>
                            </button>
                            
                            <button
                                onClick={() => setAction('')}
                                disabled={loading}
                                className="flex-1 px-4 py-3 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:bg-gray-300 transition-colors"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    // Calculate cameras for edit mode outside of conditional rendering
    const camerasToUpdate = selectedLocations.flatMap(groupKey => 
        groupedCameras.find(group => group.groupKey === groupKey)?.cameras || []
    )
    const currentCamera = camerasToUpdate[0] || {}

    // Debug: Log current render state
    console.log('Render state - action:', action, 'showDeleteConfirm:', showDeleteConfirm)

    if (action === 'edit') {
        console.log('Edit action triggered, selectedLocations:', selectedLocations)
        console.log('groupedCameras:', groupedCameras)
        console.log('camerasToUpdate:', camerasToUpdate)
        console.log('currentCamera:', currentCamera)
        
        // Add error handling
        if (!camerasToUpdate || camerasToUpdate.length === 0) {
            console.error('No cameras found for selected locations')
            return (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
                        <div className="p-6">
                            <div className="text-center">
                                <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
                                <h2 className="text-xl font-semibold text-gray-900 mb-2">No Cameras Found</h2>
                                <p className="text-gray-600 mb-4">No cameras were found for the selected locations.</p>
                                <button
                                    onClick={() => setAction('')}
                                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                                >
                                    Go Back
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )
        }
        
        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[85vh] flex flex-col">
                    <div className="flex items-center justify-between p-6 bg-gray-50 border-b border-gray-200 flex-shrink-0">
                        <div className="flex items-center space-x-3">
                            <Edit className="h-6 w-6 text-blue-600" />
                            <div>
                                <h2 className="text-xl font-semibold text-gray-900">
                                    Mass Edit Location Cameras
                                </h2>
                                <p className="text-sm text-gray-500">
                                    Update {camerasToUpdate.length} camera(s) from {selectedLocations.length} location(s)
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
                        >
                            <X className="h-5 w-5" />
                        </button>
                    </div>

                    <form onSubmit={(e) => { e.preventDefault(); handleMassEdit(); }} className="flex flex-col flex-1 min-h-0">
                        <div className="flex-1 overflow-y-auto p-6">
                            <div className="max-w-2xl mx-auto">
                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                                    <div className="flex items-center space-x-2 mb-2">
                                        <Edit className="h-5 w-5 text-blue-600" />
                                        <h3 className="text-lg font-semibold text-blue-800">Mass Edit Mode</h3>
                                    </div>
                                    <p className="text-sm text-blue-700">
                                        Update multiple cameras quickly by providing the new settings. 
                                        Only filled fields will be updated, empty fields will keep current values.
                                    </p>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    {/* IP Address */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            IP Address
                                        </label>
                                        <input
                                            type="text"
                                            value={editData.ipAddress}
                                            onChange={(e) => handleChange('ipAddress', e.target.value)}
                                            className="input w-full"
                                            placeholder={currentCamera.ipAddress || "192.168.1.100"}
                                        />
                                        {currentCamera.ipAddress && (
                                            <p className="text-xs text-gray-500 mt-1">Current: {currentCamera.ipAddress}</p>
                                        )}
                                    </div>

                                    {/* Port */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            RTSP Port
                                        </label>
                                        <input
                                            type="number"
                                            value={editData.port}
                                            onChange={(e) => handleChange('port', e.target.value)}
                                            className="input w-full"
                                            placeholder={currentCamera.port?.toString() || "554"}
                                            min="1"
                                            max="65535"
                                        />
                                        {currentCamera.port && (
                                            <p className="text-xs text-gray-500 mt-1">Current: {currentCamera.port}</p>
                                        )}
                                    </div>

                                    {/* Username */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Username
                                        </label>
                                        <input
                                            type="text"
                                            value={editData.username}
                                            onChange={(e) => handleChange('username', e.target.value)}
                                            className="input w-full"
                                            placeholder={currentCamera.username || "admin"}
                                        />
                                        {currentCamera.username && (
                                            <p className="text-xs text-gray-500 mt-1">Current: {currentCamera.username}</p>
                                        )}
                                    </div>

                                    {/* Password */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Password
                                        </label>
                                        <div className="relative">
                                            <input
                                                type={showPassword ? "text" : "password"}
                                                value={editData.password}
                                                onChange={(e) => handleChange('password', e.target.value)}
                                                className="input w-full pr-10"
                                                placeholder="Enter new password"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowPassword(!showPassword)}
                                                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700"
                                            >
                                                {showPassword ? (
                                                    <EyeOff className="h-4 w-4" />
                                                ) : (
                                                    <Eye className="h-4 w-4" />
                                                )}
                                            </button>
                                        </div>
                                        {currentCamera.password && (
                                            <p className="text-xs text-gray-500 mt-1">Current: ••••••••</p>
                                        )}
                                    </div>

                                    {/* Location Name */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Location Name
                                        </label>
                                        <input
                                            type="text"
                                            value={editData.locationName}
                                            onChange={(e) => handleChange('locationName', e.target.value)}
                                            className="input w-full"
                                            placeholder={currentCamera.location?.exactLocation || "Enter location name"}
                                        />
                                        {currentCamera.location?.exactLocation && (
                                            <p className="text-xs text-gray-500 mt-1">Current: {currentCamera.location.exactLocation}</p>
                                        )}
                                    </div>

                                    {/* Location City */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Location City
                                        </label>
                                        <input
                                            type="text"
                                            value={editData.locationCity}
                                            onChange={(e) => handleChange('locationCity', e.target.value)}
                                            className="input w-full"
                                            placeholder={currentCamera.location?.city || "Enter city name"}
                                        />
                                        {currentCamera.location?.city && (
                                            <p className="text-xs text-gray-500 mt-1">Current: {currentCamera.location.city}</p>
                                        )}
                                    </div>


                                    {/* Manufacturer */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Manufacturer
                                        </label>
                                        <input
                                            type="text"
                                            value={editData.manufacturer}
                                            onChange={(e) => handleChange('manufacturer', e.target.value)}
                                            className="input w-full"
                                            placeholder={currentCamera.manufacturer || "Hikvision"}
                                        />
                                        {currentCamera.manufacturer && (
                                            <p className="text-xs text-gray-500 mt-1">Current: {currentCamera.manufacturer}</p>
                                        )}
                                    </div>

                                    {/* Model */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Model
                                        </label>
                                        <input
                                            type="text"
                                            value={editData.model}
                                            onChange={(e) => handleChange('model', e.target.value)}
                                            className="input w-full"
                                            placeholder={currentCamera.model || "DS-2CD2xx5"}
                                        />
                                        {currentCamera.model && (
                                            <p className="text-xs text-gray-500 mt-1">Current: {currentCamera.model}</p>
                                        )}
                                    </div>

                                    {/* Resolution Width */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Resolution Width
                                        </label>
                                        <input
                                            type="number"
                                            value={editData.resolution.width}
                                            onChange={(e) => handleResolutionChange('width', e.target.value)}
                                            className="input w-full"
                                            placeholder={currentCamera.resolution?.width?.toString() || "1920"}
                                            min="640"
                                            max="7680"
                                        />
                                        {currentCamera.resolution?.width && (
                                            <p className="text-xs text-gray-500 mt-1">Current: {currentCamera.resolution.width}</p>
                                        )}
                                    </div>

                                    {/* Resolution Height */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Resolution Height
                                        </label>
                                        <input
                                            type="number"
                                            value={editData.resolution.height}
                                            onChange={(e) => handleResolutionChange('height', e.target.value)}
                                            className="input w-full"
                                            placeholder={currentCamera.resolution?.height?.toString() || "1080"}
                                            min="480"
                                            max="4320"
                                        />
                                        {currentCamera.resolution?.height && (
                                            <p className="text-xs text-gray-500 mt-1">Current: {currentCamera.resolution.height}</p>
                                        )}
                                    </div>

                                    {/* FPS */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            FPS (Frames Per Second)
                                        </label>
                                        <input
                                            type="number"
                                            value={editData.fps}
                                            onChange={(e) => handleChange('fps', parseInt(e.target.value))}
                                            className="input w-full"
                                            placeholder={currentCamera.fps?.toString() || "25"}
                                            min="1"
                                            max="60"
                                        />
                                        {currentCamera.fps && (
                                            <p className="text-xs text-gray-500 mt-1">Current: {currentCamera.fps}</p>
                                        )}
                                    </div>

                                    {/* Bitrate */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Bitrate (Kbps)
                                        </label>
                                        <input
                                            type="number"
                                            value={editData.bitrate}
                                            onChange={(e) => handleChange('bitrate', parseInt(e.target.value))}
                                            className="input w-full"
                                            placeholder={currentCamera.bitrate?.toString() || "2048"}
                                            min="64"
                                            max="8192"
                                        />
                                        {currentCamera.bitrate && (
                                            <p className="text-xs text-gray-500 mt-1">Current: {currentCamera.bitrate}</p>
                                        )}
                                    </div>
                                </div>

                                {/* Access Control */}
                                <div className="mt-6">
                                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                                        <Shield className="h-5 w-5 mr-2" />
                                        Access Control
                                    </h3>
                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Assign to Roles
                                            </label>
                                            <div className="space-y-2 max-h-32 overflow-y-auto border border-gray-200 rounded-lg p-3">
                                                {roles.map((role) => (
                                                    <label key={role._id} className="flex items-center space-x-2">
                                                        <input
                                                            type="checkbox"
                                                            checked={editData.accessRoles.includes(role._id)}
                                                            onChange={() => handleRoleChange(role._id)}
                                                            className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                                        />
                                                        <span className="text-sm text-gray-700">{role.name}</span>
                                                    </label>
                                                ))}
                                            </div>
                                            <p className="text-xs text-gray-500 mt-1">
                                                Leave unchecked to keep existing role assignments
                                            </p>
                                            {currentCamera.accessRoles && currentCamera.accessRoles.length > 0 && (
                                                <p className="text-xs text-gray-500 mt-1">
                                                    Current roles: {currentCamera.accessRoles.map(role => role.name).join(', ')}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Preview */}
                                <div className="mt-6 bg-gray-50 border border-gray-200 rounded-lg p-4">
                                    <h4 className="text-sm font-medium text-gray-700 mb-3">Preview:</h4>
                                    <div className="text-sm text-gray-600 space-y-1">
                                        <p><strong>Selected Cameras:</strong> {camerasToUpdate.length}</p>
                                        <p><strong>IP Address:</strong> {editData.ipAddress || 'Keep current'}</p>
                                        <p><strong>Port:</strong> {editData.port || 'Keep current'}</p>
                                        <p><strong>Username:</strong> {editData.username || 'Keep current'}</p>
                                        <p><strong>Password:</strong> {editData.password ? '••••••••' : 'Keep current'}</p>
                                        <p><strong>Manufacturer:</strong> {editData.manufacturer || 'Keep current'}</p>
                                        <p><strong>Model:</strong> {editData.model || 'Keep current'}</p>
                                        <p><strong>Resolution:</strong> {editData.resolution.width !== 1920 || editData.resolution.height !== 1080 ? `${editData.resolution.width}x${editData.resolution.height}` : 'Keep current'}</p>
                                        <p><strong>FPS:</strong> {editData.fps !== 25 ? editData.fps : 'Keep current'}</p>
                                        <p><strong>Bitrate:</strong> {editData.bitrate !== 2048 ? editData.bitrate + ' Kbps' : 'Keep current'}</p>
                                        <p><strong>Location Name:</strong> {editData.locationName || 'Keep current'}</p>
                                        <p><strong>Location City:</strong> {editData.locationCity || 'Keep current'}</p>
                                        <p><strong>Roles:</strong> {editData.rolesChanged ? (editData.accessRoles.length > 0 ? editData.accessRoles.length + ' selected' : 'Clear all roles') : 'Keep current'}</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="flex items-center justify-between p-6 bg-gray-50 border-t border-gray-200 flex-shrink-0">
                            <div className="text-sm text-gray-600">
                                {selectedLocations.length} {groupingMode === 'individual' ? 'camera(s)' : groupingMode === 'nvr' ? 'NVR(s)' : 'location(s)'} selected, {camerasToUpdate.length} camera(s) total
                            </div>
                            <div className="flex items-center space-x-3">
                                <button
                                    type="button"
                                    onClick={() => setAction('')}
                                    className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 transition-colors flex items-center space-x-2"
                                >
                                    {loading ? (
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                    ) : (
                                        <Save className="h-4 w-4" />
                                    )}
                                    <span>{loading ? 'Updating...' : 'Update Cameras'}</span>
                                </button>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        )
    }

    // Delete Confirmation Modal
    if (showDeleteConfirm) {
        console.log('Delete confirmation modal shown')
        const groupedCamerasForDelete = getGroupedCameras()
        const camerasToDelete = selectedLocations.flatMap(groupKey => 
            groupedCamerasForDelete.find(group => group.groupKey === groupKey)?.cameras || []
        )
        console.log('groupedCamerasForDelete:', groupedCamerasForDelete)
        console.log('camerasToDelete:', camerasToDelete)
        
        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl">
                    <div className="p-6">
                        <div className="flex items-center space-x-3 mb-4">
                            <AlertTriangle className="h-6 w-6 text-red-600" />
                            <h2 className="text-xl font-semibold text-gray-900">
                                Confirm Location Deletion
                            </h2>
                        </div>
                        <p className="text-gray-600 mb-6">
                            Are you sure you want to delete all cameras from <strong>{selectedLocations.length}</strong> location(s)? 
                            This will delete <strong>{camerasToDelete.length}</strong> camera(s) and cannot be undone.
                        </p>
                        
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                            <h4 className="font-medium text-red-800 mb-2">Locations to be deleted:</h4>
                            <div className="max-h-32 overflow-y-auto space-y-2">
                                {selectedLocations.map(groupKey => {
                                    const group = groupedCameras.find(g => g.groupKey === groupKey)
                                    return (
                                        <div key={groupKey} className="text-sm text-red-700">
                                            <strong>{group?.groupName}</strong> - {group?.cameras.length} camera(s)
                                        </div>
                                    )
                                })}
                            </div>
                        </div>
                        
                        <div className="flex space-x-3">
                            <button
                                onClick={() => {
                                    console.log('Delete button clicked in modal')
                                    handleMassDelete()
                                }}
                                disabled={loading}
                                className="flex-1 flex items-center justify-center space-x-2 px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-gray-400 transition-colors"
                            >
                                {loading ? (
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                ) : (
                                    <Trash2 className="h-4 w-4" />
                                )}
                                <span>{loading ? 'Deleting...' : 'Delete All Cameras'}</span>
                            </button>
                            
                            <button
                                onClick={() => setShowDeleteConfirm(false)}
                                disabled={loading}
                                className="flex-1 px-4 py-3 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:bg-gray-300 transition-colors"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    return null
}

export default NVRMassActions 